"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { clsx } from "clsx";
import { motion, AnimatePresence, Variants } from "framer-motion";
import ScrollProgress from "./ScrollProgress";

const navLinks = [
  { name: "Termine", href: "/#termine" },
  { name: "Musik", href: "/#musik" },
  { name: "Band", href: "/#band" },
  { name: "Galerie", href: "/#galerie" },
  { name: "Social Media", href: "/#social-media" },
  { name: "Presse", href: "/archiv#presse" },
  { name: "Gig-Archiv", href: "/archiv#archiv-liste" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Burger Menu Animation Variants
  const menuVariants: Variants = {
    closed: { 
      x: "100%",
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    open: { 
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 30, staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const linkVariants: Variants = {
    closed: { opacity: 0, x: 50 },
    open: { opacity: 1, x: 0 }
  };

  return (
    <header className={clsx(
      "main-header transition-all duration-300",
      isScrolled ? "bg-black/80 backdrop-blur-md shadow-lg" : "transparent-header"
    )}>
      <nav className="navbar relative">
        <div className="logo z-50">
          <Link 
            href="/" 
            className="flex items-center gap-4" 
            onClick={(e) => {
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
              setIsOpen(false);
            }}
          >
            <Image 
              src="/icon.svg" 
              alt="Becoming Wind Logo" 
              width={48} 
              height={48} 
              className="band-logo"
            />
            <span className="text-2xl font-bold uppercase tracking-widest hidden sm:inline font-oswald text-accent">
              Becoming Wind
            </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <ul className="nav-links hidden md:flex">
          {navLinks.map((link) => (
            <li key={link.name}>
              <Link 
                href={link.href} 
                className="hover:text-accent transition-colors relative group"
              >
                {link.name}
                <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-secondary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
          ))}
        </ul>

        {/* Burger Button */}
        <div 
          className="burger-menu md:hidden z-50 flex flex-col justify-around w-8 h-8 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <motion.div 
            animate={isOpen ? { rotate: 45, y: 9 } : { rotate: 0, y: 0 }} 
            className="w-full h-[3px] bg-textLight rounded-full origin-center"
          />
          <motion.div 
            animate={isOpen ? { opacity: 0 } : { opacity: 1 }} 
            className="w-full h-[3px] bg-textLight rounded-full"
          />
          <motion.div 
            animate={isOpen ? { rotate: -45, y: -9 } : { rotate: 0, y: 0 }} 
            className="w-full h-[3px] bg-textLight rounded-full origin-center"
          />
        </div>

        {/* Mobile Navigation Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="fixed top-0 right-0 w-[80%] max-w-[400px] h-screen bg-black/95 backdrop-blur-xl z-40 flex flex-col items-center justify-center shadow-2xl border-l border-white/10"
            >
              <ul className="flex flex-col gap-8 text-center">
                {navLinks.map((link) => (
                  <motion.li key={link.name} variants={linkVariants}>
                    <Link 
                      href={link.href} 
                      onClick={() => setIsOpen(false)}
                      className="text-3xl font-oswald text-accent hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      <ScrollProgress />
    </header>
  );
}